import { useEffect, useState } from "react";
import { Navigate, Routes, Route } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import DashboardLayout from "../components/DashboardLayout";
import ContentCMS from "./ContentCMS";
import SponsorsAdmin from "./SponsorsAdmin";
import { Building2, Users, Calendar, Star, Sparkles, AlertTriangle, DollarSign, Mail, Eye, X, Download, BarChart3 } from "lucide-react";
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Line } from "recharts";

function StatCard({ label, value, accent, icon: Icon }) {
  return (
    <div data-testid={`super-stat-${label}`} className="stat-card">
      <div className="flex items-start justify-between">
        <div className="overline text-[11px]">{label}</div>
        {Icon && <Icon size={16} className="text-[#C5A059]" />}
      </div>
      <div className={`mt-4 font-serif text-3xl ${accent ? "text-[#C5A059]" : "text-[#F5F0EB]"}`}>{value}</div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [confirm, setConfirm] = useState(null);

  const load = async () => { const { data } = await api.get("/stats/global"); setStats(data); };
  useEffect(() => { load(); }, []);

  const toggle = async (id, current) => {
    await api.patch(`/admin/cafes/${id}/status`, { status: current === "active" ? "suspended" : "active" });
    await load();
  };
  const toggleSponsored = async (id, current) => {
    await api.patch(`/admin/cafes/${id}/sponsored`, { sponsored: !current });
    await load();
  };
  const cancelSub = async (id) => {
    await api.post(`/admin/cafes/${id}/cancel-subscription`);
    await load();
  };
  const activateManually = async (id, plan_id) => {
    await api.post(`/admin/cafes/${id}/activate-manually`, { plan_id });
    await load();
  };
  const deleteCafe = async (id) => {
    await api.delete(`/admin/cafes/${id}`);
    setConfirm(null);
    await load();
  };

  if (!stats) return <div className="p-8 text-[#A8A39D]">...</div>;

  const visible = stats.cafes.filter((c) => {
    if (statusFilter !== "all" && c.status !== statusFilter && c.subscription_status !== statusFilter) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (c.name?.toLowerCase().includes(s) || c.city?.toLowerCase().includes(s) || c.slug?.toLowerCase().includes(s));
  });

  return (
    <div className="p-8 lg:p-12">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline mb-2">Super Admin</div>
          <h1 className="font-serif text-4xl md:text-5xl font-light">Vue globale</h1>
        </div>
        {stats.alerts_unread > 0 && (
          <div className="px-4 py-2 border border-orange-400/40 text-orange-400 text-xs tracking-widest uppercase flex items-center gap-2">
            <AlertTriangle size={14} /> {stats.alerts_unread} alertes non lues
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <StatCard label="Commerces" value={stats.total_cafes} icon={Building2} />
        <StatCard label="Actifs" value={stats.active_cafes} accent icon={Building2} />
        <StatCard label="Premium" value={stats.premium_cafes} accent icon={Star} />
        <StatCard label="Standard" value={stats.standard_cafes} icon={Sparkles} />
        <StatCard label="Réservations" value={stats.total_reservations} icon={Calendar} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-12">
        <StatCard label="En attente paiement" value={stats.incomplete_cafes} icon={Sparkles} />
        <StatCard label="Paiement en retard" value={stats.past_due_cafes} icon={AlertTriangle} />
        <StatCard label="Suspendus" value={stats.suspended_cafes} icon={X} />
        <StatCard label="Revenu mois ($ CAD)" value={`${stats.revenue_month_cad.toFixed(0)}$`} accent icon={DollarSign} />
        <StatCard label="Utilisateurs" value={stats.total_users} icon={Users} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <input data-testid="cafe-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (nom, ville, slug)..." className="luxe-input flex-1 min-w-[260px]" />
        <select data-testid="status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-[#1C1916] border border-white/10 px-4 py-2 text-xs uppercase tracking-widest text-[#F5F0EB]">
          <option value="all">Tous</option>
          <option value="active">Actif</option>
          <option value="suspended">Suspendu</option>
          <option value="incomplete">Incomplete</option>
          <option value="past_due">Past due</option>
          <option value="trial">Essai</option>
        </select>
      </div>

      <div data-testid="cafes-table" className="stat-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#A8A39D] tracking-widest text-xs uppercase border-b border-white/5">
              <th className="py-3">Nom</th><th>Ville</th><th>Plan</th><th>Abonnement</th><th>Statut</th><th>Sponsor</th><th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((c) => (
              <tr key={c.id} data-testid={`cafe-row-${c.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="py-3">
                  <div className="font-serif text-base">{c.name}</div>
                  <div className="text-[10px] text-[#A8A39D] tracking-widest uppercase">/{c.slug}</div>
                </td>
                <td className="text-[#A8A39D]">{c.city || "—"}</td>
                <td><span className={`px-2 py-1 text-[10px] uppercase tracking-widest border ${c.plan_id === 'premium' ? 'border-[#C5A059] text-[#C5A059]' : c.plan_id === 'standard' ? 'border-white/20 text-[#F5F0EB]' : 'border-white/10 text-[#A8A39D]'}`}>{c.plan_id || "—"}</span></td>
                <td><span className="px-2 py-1 border border-white/10 text-[10px] uppercase tracking-widest text-[#A8A39D]">{c.subscription_status}</span></td>
                <td><span className={`px-2 py-1 text-[10px] uppercase tracking-widest border ${c.status === "active" ? "text-[#C5A059] border-[#C5A059]/40" : "text-red-400 border-red-400/40"}`}>{c.status}</span></td>
                <td>
                  <button data-testid={`sponsor-${c.id}`} onClick={() => toggleSponsored(c.id, c.sponsored)} className={`px-2 py-1 text-[10px] uppercase tracking-widest border transition-colors ${c.sponsored ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5" : "border-white/10 text-[#A8A39D] hover:text-[#F5F0EB]"}`}>{c.sponsored ? "Oui" : "Non"}</button>
                </td>
                <td className="text-right">
                  <div className="inline-flex flex-wrap items-center gap-2 justify-end">
                    {c.subscription_status === "incomplete" && (
                      <>
                        <button data-testid={`manual-std-${c.id}`} onClick={() => activateManually(c.id, "standard")} className="text-[10px] uppercase tracking-widest text-[#C5A059] hover:text-[#D4AF37]">Activer Std</button>
                        <button data-testid={`manual-prem-${c.id}`} onClick={() => activateManually(c.id, "premium")} className="text-[10px] uppercase tracking-widest text-[#C5A059] hover:text-[#D4AF37]">Activer Prem</button>
                      </>
                    )}
                    {c.subscription_id && <button data-testid={`cancel-sub-${c.id}`} onClick={() => cancelSub(c.id)} className="text-[10px] uppercase tracking-widest text-orange-400 hover:text-orange-300">Annuler abo</button>}
                    <button data-testid={`toggle-${c.id}`} onClick={() => toggle(c.id, c.status)} className="text-[10px] uppercase tracking-widest text-[#C5A059] hover:text-[#D4AF37]">{c.status === "active" ? "Suspendre" : "Activer"}</button>
                    <button data-testid={`delete-${c.id}`} onClick={() => setConfirm(c)} className="text-[10px] uppercase tracking-widest text-red-400 hover:text-red-300">Supprimer</button>
                  </div>
                </td>
              </tr>
            ))}
            {!visible.length && <tr><td colSpan={7} className="py-12 text-center text-[#A8A39D]">Aucun commerce</td></tr>}
          </tbody>
        </table>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setConfirm(null)}>
          <div className="bg-[#0E0C0B] border border-white/10 p-8 max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="overline mb-3 text-red-400">Confirmation requise</div>
            <h3 className="font-serif text-2xl mb-3">Supprimer <strong>{confirm.name}</strong> ?</h3>
            <p className="text-[#A8A39D] text-sm mb-6">Cette action supprime le commerce, ses produits, réservations, vues et compte propriétaire. L'abonnement Stripe sera annulé. Action irréversible.</p>
            <div className="flex gap-3">
              <button data-testid="confirm-delete" onClick={() => deleteCafe(confirm.id)} className="px-6 py-3 bg-red-500 text-white text-xs tracking-widest uppercase">Supprimer</button>
              <button onClick={() => setConfirm(null)} className="btn-ghost">Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Subscriptions() {
  const [subs, setSubs] = useState([]);
  useEffect(() => { api.get("/admin/subscriptions").then((r) => setSubs(r.data)); }, []);
  return (
    <div className="p-8 lg:p-12">
      <div className="overline mb-2">Super Admin</div>
      <h1 className="font-serif text-4xl font-light mb-10">Abonnements</h1>
      <div data-testid="subs-table" className="stat-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#A8A39D] tracking-widest text-xs uppercase border-b border-white/5">
              <th className="py-3">Commerce</th><th>Ville</th><th>Plan</th><th>Statut</th><th>Activé le</th><th>Grâce jusqu'au</th><th>Stripe Sub ID</th>
            </tr>
          </thead>
          <tbody>
            {subs.map((s) => (
              <tr key={s.cafe_id} className="border-b border-white/5">
                <td className="py-3 font-serif text-base">{s.cafe_name}</td>
                <td className="text-[#A8A39D]">{s.city || "—"}</td>
                <td><span className={`px-2 py-1 text-[10px] uppercase tracking-widest border ${s.plan_id === 'premium' ? 'border-[#C5A059] text-[#C5A059]' : 'border-white/20 text-[#F5F0EB]'}`}>{s.plan_id || "—"}</span></td>
                <td><span className="text-[10px] uppercase tracking-widest text-[#A8A39D]">{s.subscription_status}</span></td>
                <td className="text-[#A8A39D] text-xs">{s.activated_at ? s.activated_at.slice(0, 10) : "—"}</td>
                <td className="text-orange-400 text-xs">{s.grace_period_until ? s.grace_period_until.slice(0, 10) : "—"}</td>
                <td className="text-[10px] text-[#A8A39D] font-mono">{s.subscription_id?.slice(0, 20)}...</td>
              </tr>
            ))}
            {!subs.length && <tr><td colSpan={7} className="py-10 text-center text-[#A8A39D]">Aucun abonnement</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Payments() {
  const [txs, setTxs] = useState([]);
  useEffect(() => { api.get("/admin/payments").then((r) => setTxs(r.data)); }, []);
  return (
    <div className="p-8 lg:p-12">
      <div className="overline mb-2">Super Admin</div>
      <h1 className="font-serif text-4xl font-light mb-10">Paiements</h1>
      <div data-testid="payments-table" className="stat-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#A8A39D] tracking-widest text-xs uppercase border-b border-white/5">
              <th className="py-3">Date</th><th>Type</th><th>Commerce</th><th>Montant</th><th>Statut</th><th>Stripe</th>
            </tr>
          </thead>
          <tbody>
            {txs.map((t) => (
              <tr key={t.id} className="border-b border-white/5">
                <td className="py-3 text-[#A8A39D] text-xs">{(t.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td><span className="text-[10px] uppercase tracking-widest text-[#A8A39D]">{t.type || "checkout"}</span></td>
                <td className="text-xs">{t.cafe_id?.slice(0, 8)}...</td>
                <td className="font-serif text-[#C5A059]">{(t.amount_cad || 0).toFixed(2)} $</td>
                <td><span className={`px-2 py-1 text-[10px] uppercase tracking-widest border ${t.payment_status === 'paid' ? 'border-[#C5A059] text-[#C5A059]' : t.payment_status === 'failed' ? 'border-red-400 text-red-400' : 'border-white/10 text-[#A8A39D]'}`}>{t.payment_status || t.status}</span></td>
                <td>{t.hosted_invoice_url ? <a href={t.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="text-[#C5A059] text-[10px] uppercase tracking-widest hover:underline"><Eye size={10} className="inline" /> Voir</a> : <span className="text-[#A8A39D] text-[10px]">—</span>}</td>
              </tr>
            ))}
            {!txs.length && <tr><td colSpan={6} className="py-10 text-center text-[#A8A39D]">Aucun paiement</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const load = async () => { const { data } = await api.get("/admin/alerts"); setAlerts(data); };
  useEffect(() => { load(); }, []);
  const markRead = async (id) => { await api.post(`/admin/alerts/${id}/read`); await load(); };
  return (
    <div className="p-8 lg:p-12">
      <div className="overline mb-2">Super Admin</div>
      <h1 className="font-serif text-4xl font-light mb-10">Alertes système</h1>
      <div className="space-y-3">
        {alerts.map((a) => (
          <div key={a.id} data-testid={`alert-${a.id}`} className={`stat-card flex items-start gap-4 ${a.read ? "opacity-50" : ""}`}>
            <div className={`mt-1 ${a.severity === 'critical' ? 'text-red-400' : a.severity === 'warning' ? 'text-orange-400' : 'text-[#C5A059]'}`}>
              <AlertTriangle size={18} />
            </div>
            <div className="flex-1">
              <div className="overline text-[10px]">{a.type} · {a.severity}</div>
              <div className="text-sm mt-1">{a.message}</div>
              <div className="text-[10px] text-[#A8A39D] mt-1">{a.created_at.slice(0, 16).replace("T", " ")}</div>
            </div>
            {!a.read && <button onClick={() => markRead(a.id)} className="text-[10px] uppercase tracking-widest text-[#C5A059] hover:text-[#D4AF37]">Marquer lu</button>}
          </div>
        ))}
        {!alerts.length && <div className="stat-card text-center text-[#A8A39D] py-12">Aucune alerte</div>}
      </div>
    </div>
  );
}

function EmailOutbox() {
  const [emails, setEmails] = useState([]);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const refresh = () => api.get("/admin/email-outbox").then((r) => setEmails(r.data));
  useEffect(() => {
    refresh();
    api.get("/admin/email-status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  const viewFull = async (id) => {
    const { data } = await api.get(`/admin/email-outbox/${id}`);
    setSelected(data);
  };
  const sendTest = async () => {
    if (!testTo.includes("@")) return;
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/admin/email-test", { to: testTo });
      setTestResult(data);
      await refresh();
    } catch (e) {
      setTestResult({ status: "failed", delivery: e?.response?.data?.detail || "Erreur" });
    } finally { setTesting(false); }
  };
  return (
    <div className="p-8 lg:p-12">
      <div className="overline mb-2">Super Admin</div>
      <h1 className="font-serif text-4xl font-light mb-3">Emails</h1>
      <p className="text-[#A8A39D] text-sm mb-10 max-w-2xl">Tous les emails envoyés par CAFÉ-SPOT sont historisés ici. Utilisez le bouton ci-dessous pour tester votre configuration Resend.</p>

      <div data-testid="email-status-card" className="stat-card mb-8">
        <div className="overline mb-4">Configuration Resend</div>
        {status ? (
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#A8A39D] mb-1">Statut</div>
              <div className={status.resend_configured ? "text-[#C5A059]" : "text-orange-400"}>
                {status.resend_configured ? "✓ Resend configuré (envoi réel)" : "○ Mode console (RESEND_API_KEY vide)"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#A8A39D] mb-1">Expéditeur</div>
              <div className="text-[#F5F0EB]">{status.sender_name} &lt;{status.sender_email}&gt;</div>
            </div>
            {status.api_key_masked && (
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-[#A8A39D] mb-1">Clé API</div>
                <div className="text-[#F5F0EB] font-mono text-xs">{status.api_key_masked}</div>
              </div>
            )}
          </div>
        ) : <div className="text-[#A8A39D] text-sm">Chargement...</div>}
        <div className="mt-6 flex flex-col md:flex-row gap-3">
          <input
            data-testid="email-test-to"
            type="email"
            placeholder="Adresse de test (ex : support@cafe-spot.ca)"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            className="luxe-input flex-1"
          />
          <button
            data-testid="email-test-send"
            onClick={sendTest}
            disabled={testing || !testTo.includes("@")}
            className="btn-gold disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {testing ? "Envoi..." : "Envoyer un email test"}
          </button>
        </div>
        {testResult && (
          <div data-testid="email-test-result" className={`mt-4 text-sm ${testResult.status === "sent" ? "text-[#C5A059]" : testResult.status === "console" ? "text-orange-400" : "text-red-400"}`}>
            {testResult.status === "sent" && <>✓ Envoyé avec succès. ID Resend : <code className="text-xs">{testResult.delivery}</code></>}
            {testResult.status === "console" && <>○ Mode console — l'email n'est PAS envoyé réellement. Configurez RESEND_API_KEY dans le backend.</>}
            {testResult.status === "failed" && <>✗ Échec : {testResult.delivery}</>}
          </div>
        )}
      </div>

      <div className="stat-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#A8A39D] tracking-widest text-xs uppercase border-b border-white/5">
              <th className="py-3">Date</th><th>Destinataire</th><th>Sujet</th><th>Type</th><th>Statut</th><th></th>
            </tr>
          </thead>
          <tbody>
            {emails.map((e) => (
              <tr key={e.id} className="border-b border-white/5">
                <td className="py-3 text-[#A8A39D] text-xs">{(e.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="text-xs">{e.to}</td>
                <td className="text-sm">{e.subject}</td>
                <td><span className="text-[10px] uppercase tracking-widest text-[#A8A39D]">{e.kind}</span></td>
                <td><span className={`text-[10px] uppercase tracking-widest ${e.status === 'sent' ? 'text-[#C5A059]' : e.status === 'console' ? 'text-orange-400' : e.status === 'failed' ? 'text-red-400' : 'text-[#A8A39D]'}`}>{e.status}</span></td>
                <td><button onClick={() => viewFull(e.id)} className="text-[#C5A059] text-[10px] uppercase tracking-widest"><Eye size={10} className="inline" /> Voir</button></td>
              </tr>
            ))}
            {!emails.length && <tr><td colSpan={6} className="py-10 text-center text-[#A8A39D]">Aucun email</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6" onClick={() => setSelected(null)}>
          <div className="bg-[#0E0C0B] border border-white/10 max-w-3xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-white/5 flex items-center justify-between">
              <div>
                <div className="overline">À : {selected.to}</div>
                <div className="font-serif text-xl mt-1">{selected.subject}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-[#A8A39D] hover:text-[#F5F0EB]"><X size={20} /></button>
            </div>
            <div className="p-6" dangerouslySetInnerHTML={{ __html: selected.html }} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function SuperAdmin() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#A8A39D]">...</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/super-admin" }} />;
  if (user.role !== "super_admin") return <Navigate to="/" />;
  return (
    <DashboardLayout isSuper>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="subscriptions" element={<Subscriptions />} />
        <Route path="payments" element={<Payments />} />
        <Route path="invoices" element={<AdminInvoices />} />
        <Route path="visitors" element={<Visitors />} />
        <Route path="content" element={<ContentCMS />} />
        <Route path="sponsors" element={<SponsorsAdmin />} />
        <Route path="alerts" element={<Alerts />} />
        <Route path="emails" element={<EmailOutbox />} />
      </Routes>
    </DashboardLayout>
  );
}

function AdminInvoices() {
  const [invs, setInvs] = useState([]);
  useEffect(() => { api.get("/admin/invoices").then((r) => setInvs(r.data)); }, []);
  const download = async (id, number) => {
    const token = localStorage.getItem("access_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${number}.pdf`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <div className="p-8 lg:p-12">
      <div className="overline mb-2">Super Admin</div>
      <h1 className="font-serif text-4xl font-light mb-10">Factures émises</h1>
      <div data-testid="admin-invoices-table" className="stat-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#A8A39D] tracking-widest text-xs uppercase border-b border-white/5">
              <th className="py-3">N°</th><th>Date</th><th>Commerce</th><th>Plan</th><th>Base</th><th>TPS</th><th>TVQ</th><th>Total</th><th></th>
            </tr>
          </thead>
          <tbody>
            {invs.map((i) => (
              <tr key={i.id} className="border-b border-white/5">
                <td className="py-3 text-[#C5A059] font-serif">{i.invoice_number}</td>
                <td className="text-[#A8A39D] text-xs">{i.issued_at.slice(0, 10)}</td>
                <td className="text-xs">{i.cafe_id?.slice(0, 8)}...</td>
                <td><span className="text-[10px] uppercase tracking-widest text-[#A8A39D]">{i.plan_id}</span></td>
                <td>{i.base_cad.toFixed(2)} $</td>
                <td className="text-[#A8A39D]">{i.tps_cad.toFixed(2)} $</td>
                <td className="text-[#A8A39D]">{i.tvq_cad.toFixed(2)} $</td>
                <td className="font-serif text-[#C5A059]">{i.total_cad.toFixed(2)} $</td>
                <td><button onClick={() => download(i.id, i.invoice_number)} className="text-[#C5A059] p-2"><Download size={14} /></button></td>
              </tr>
            ))}
            {!invs.length && <tr><td colSpan={9} className="py-10 text-center text-[#A8A39D]">Aucune facture émise. Elles se génèrent automatiquement lors des paiements Stripe.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Visitors() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/visitor-stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div className="p-8 text-[#A8A39D]">...</div>;
  return (
    <div className="p-8 lg:p-12">
      <div className="overline mb-2">Super Admin</div>
      <h1 className="font-serif text-4xl font-light mb-10">Statistiques visiteurs</h1>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        <StatCard label="Vues totales" value={stats.total_views} accent icon={BarChart3} />
        <StatCard label="Vues ce mois" value={stats.views_month} icon={Eye} />
        <StatCard label="Top commerces" value={stats.top_cafes.length} icon={Star} />
      </div>

      <div className="stat-card mb-10">
        <div className="overline mb-6">Vues sur 30 jours</div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={stats.views_series}>
            <CartesianGrid stroke="rgba(245,240,235,0.05)" strokeDasharray="3 3" />
            <XAxis dataKey="date" stroke="#A8A39D" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
            <YAxis stroke="#A8A39D" tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#141210", border: "1px solid rgba(197,160,89,0.3)", borderRadius: 0 }} labelStyle={{ color: "#C5A059" }} />
            <Line type="monotone" dataKey="count" stroke="#C5A059" strokeWidth={2} dot={{ fill: "#C5A059", r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="stat-card">
        <div className="overline mb-6">Top 10 commerces (30 jours)</div>
        {stats.top_cafes.length === 0 && <div className="text-[#A8A39D] text-sm py-4">Aucune vue enregistrée.</div>}
        {stats.top_cafes.map((row, idx) => (
          <div key={row.cafe.slug} className="py-3 border-b border-white/5 flex items-center gap-4">
            <div className="font-serif text-2xl text-[#C5A059] w-10">{idx + 1}</div>
            <div className="flex-1">
              <div className="font-serif text-lg">{row.cafe.name}</div>
              <div className="text-xs text-[#A8A39D]">{row.cafe.city} · {row.cafe.category} {row.cafe.plan_id === "premium" && <span className="ml-2 text-[#C5A059]">★ Premium</span>}</div>
            </div>
            <div className="font-serif text-xl text-[#F5F0EB]">{row.views} <span className="text-xs text-[#A8A39D]">vues</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}
